import { useState } from 'react';
import { Button } from '@simplywallst/ui-core/next';
import ActionSheet, { Background, Sheet } from '@components/ActionSheet';
import CrossSolid from '@simplywallst/ui-core/icons/CrossSolid';

import WelcomeStep from './WelcomeStep';
import CreateProfileStep from './CreateProfileStep';
import Dot from './Dot';
import useUser from '@/hooks/useUser';
import { UserHttpError } from '@/hooks/useUser/userHttpErrors';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  canCancel?: boolean;
  trackEvent: (event: { action: string }) => void;
}

const NarrativeOnboardingModal = ({
  isOpen,
  onClose,
  canCancel = true,
  trackEvent,
}: Props) => {
  const [step, setStep] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [username, setUsername] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const {
    data: user,
    mutate,
    mutationStatus,
  } = useUser({
    onMutationSuccess: () => {
      setHasError(false);
      onClose();
      trackEvent({
        action: 'onboarding-completed',
      });
    },
    onMutationError: (mutationError: unknown) => {
      if (mutationError instanceof UserHttpError) {
        setErrorMessage('Display name already taken');
        setHasError(true);
      } else {
        setErrorMessage('The operation failed.');
        setHasError(true);
      }
    },
  });

  const updateUserDisplayName = () => {
    if (!hasError && username !== user?.displayName && username.length > 0) {
      setErrorMessage(undefined);
      mutate({ display_name: username });
    }
  };

  return (
    <ActionSheet open={isOpen} onClose={canCancel ? onClose : () => {}}>
      <Background onClick={canCancel ? onClose : () => {}} />
      <div className="px-2 lg:px-0">
        <Sheet
          sheetLayout={{
            'max-width': '421px',
            height: '546px',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <div
            className="overflow-hidden px-2 scrollbar-hidden lg:px-0"
            data-testid="narrative-onboarding-modal"
          >
            <div className="border relative flex h-full flex-col items-center justify-start gap-x3 rounded bg-surface-1 p-12 scrollbar-hidden">
              {canCancel && (
                <Button
                  variant="subtle"
                  size="medium"
                  className="absolute right-3 top-3 size-x4 p-0"
                  aria-label="Close"
                  onClick={onClose}
                >
                  <CrossSolid className="size-x4 fill-solid" />
                </Button>
              )}
              {step === 0 && <WelcomeStep />}
              {step === 1 && (
                <CreateProfileStep
                  setHasError={setHasError}
                  username={username}
                  mutationStatus={mutationStatus}
                  resetMutationError={() => setErrorMessage(undefined)}
                  mutationErrorMessage={errorMessage}
                  setUsername={setUsername}
                />
              )}
              <div
                className={`flex justify-center ${step === 1 ? 'mt-auto' : ''}`}
              >
                <div className="flex w-[325px] flex-col items-center gap-x2">
                  <Button
                    variant="primary"
                    size="large"
                    isDisabled={step === 1 ? hasError : false}
                    onClick={() => {
                      if (step === 0) {
                        setStep(1);
                      } else {
                        updateUserDisplayName();
                      }
                    }}
                    className="w-full"
                  >
                    Next
                  </Button>
                  <div className="grid grid-cols-[max-content_max-content] justify-center gap-x1">
                    <Dot onClick={() => setStep(0)} active={step === 0} />
                    <Dot onClick={() => setStep(1)} active={step === 1} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Sheet>
      </div>
    </ActionSheet>
  );
};

export { NarrativeOnboardingModal };
